import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi, type RulesSection } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';

type Form = {
  id?: number;
  title: string;
  itemsText: string;
  sortOrder: number;
  isVisible: boolean;
};

const empty = (): Form => ({ title: '', itemsText: '', sortOrder: 0, isVisible: true });

function parseItems(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function AdminRules() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'rules'], queryFn: adminApi.rules.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(empty());

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'rules'] });
    qc.invalidateQueries({ queryKey: ['rules'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: form.title,
        items: form.itemsText.split('\n').map((s) => s.trim()).filter(Boolean),
        sortOrder: form.sortOrder,
        isVisible: form.isVisible,
      };
      return form.id ? adminApi.rules.update(form.id, payload) : adminApi.rules.create(payload);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      invalidate();
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.rules.remove(id),
    onSuccess: () => {
      toast.success('Удалено');
      invalidate();
    },
  });

  function openEdit(s: RulesSection) {
    setForm({
      id: s.id,
      title: s.title ?? '',
      itemsText: parseItems(s.items).join('\n'),
      sortOrder: s.sortOrder ?? 0,
      isVisible: s.isVisible ?? true,
    });
    setOpen(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Правила</h1>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Добавить раздел
        </Button>
      </div>

      {!data?.length ? (
        <p className="text-eol-desc">Пока пусто.</p>
      ) : (
        <div className="space-y-3">
          {data.map((s) => {
            const items = parseItems(s.items);
            return (
              <Card key={s.id}>
                <CardContent className="pt-4 pb-4 flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-eol-white">{s.title}</p>
                    <ul className="mt-2 text-sm text-eol-desc space-y-1">
                      {items.map((it, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-eol-pink">•</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    {!s.isVisible && <Badge variant="danger" className="mt-2">Скрыт</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="secondary" onClick={() => openEdit(s)} title="Редактировать">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => { if (confirm(`Удалить «${s.title}»?`)) remove.mutate(s.id); }}
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать раздел' : 'Новый раздел'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Название раздела"
            />
            <label className="block">
              <span className="text-xs uppercase font-semibold tracking-wider text-eol-desc">
                Пункты (по одному на строку)
              </span>
              <textarea
                rows={8}
                className="mt-1 w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white"
                value={form.itemsText}
                onChange={(e) => setForm({ ...form, itemsText: e.target.value })}
                placeholder={'Уважайте других игроков.\nЗапрещены оскорбления.'}
              />
            </label>
            <div className="flex gap-3 items-center flex-wrap">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-eol-desc">Порядок</span>
                <NumberInput value={form.sortOrder} onChange={(v) => setForm({ ...form, sortOrder: v })} step={10} className="w-28" />
              </label>
              <label className="flex items-center gap-2 text-eol-white mt-5">
                <input
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                />
                Показывать
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
